// «Difusión» de la app WhatsApp: Contactos · Plantillas · Envíos masivos.
// Reúne lo que antes era el módulo suelto «Difusión WhatsApp» (/m/whatsapp/*,
// mcp_django) y las plantillas que estaban en Configuración. Su «Inbox» NO se
// trae: duplicaba «Chats» (las conversaciones viven en Orión).
//
// Gating: Contactos y Envíos masivos son el módulo contratable `whatsapp`
// (backend /org/modules). Si la org no lo tiene, no se ofrecen y se dice por
// qué; las plantillas aprobadas (Orión, en vivo de Meta/Twilio) siempre se ven.

import { useEffect, useState } from 'react';
import { ChevronDown, Contact, FileText, Info, Send } from 'lucide-react';

import { useModules } from '@/context/ModulesContext';
import { WhatsAppBroadcastsPage } from '@/components/whatsapp/WhatsAppBroadcastsPage';
import { WhatsAppContactsPage } from '@/components/whatsapp/WhatsAppContactsPage';
import { WhatsAppTemplatesPage } from '@/components/whatsapp/WhatsAppTemplatesPage';
import { getTemplates, type WaTemplate } from '../api';
import { Card, H, SectionLayout, useSection, type Section } from './Sections';

type Notify = (type: 'success' | 'error' | 'info', title: string, message?: string) => void;
type Key = 'contactos' | 'plantillas' | 'envios';

const ALL: Section<Key>[] = [
  { key: 'contactos', label: 'Contactos', Icon: Contact },
  { key: 'plantillas', label: 'Plantillas', Icon: FileText },
  { key: 'envios', label: 'Envíos masivos', short: 'Envíos', Icon: Send },
];

export function DifusionView({ notify, isMobile }: { notify: Notify; isMobile: boolean }) {
  const { isEnabled } = useModules();
  const difusion = (() => { try { return isEnabled('whatsapp'); } catch { return false; } })();
  const sections = difusion ? ALL : ALL.filter((s) => s.key === 'plantillas');
  const [cur, setCur] = useSection(sections);
  // Las páginas de mcp_django notifican con {type,title,message}.
  const fwd = (n: { type: 'success' | 'error' | 'info'; title: string; message?: string }) => notify(n.type, n.title, n.message);
  return (
    <SectionLayout label="Difusión por WhatsApp" sections={sections} current={cur} onSelect={setCur} isMobile={isMobile} wide={cur !== 'plantillas'}>
      {cur === 'contactos' && (
        <>
          <H title="Contactos" sub="Tus contactos de WhatsApp, con su permiso para recibir mensajes y etiquetas para segmentar los envíos." />
          <WhatsAppContactsPage embedded addNotification={fwd} />
        </>
      )}
      {cur === 'plantillas' && <TemplatesSection notify={notify} manage={difusion} />}
      {cur === 'envios' && (
        <>
          <H title="Envíos masivos" sub="Manda una plantilla aprobada a un grupo de contactos (por permiso y etiquetas)." />
          <WhatsAppBroadcastsPage embedded addNotification={fwd} />
        </>
      )}
      {!difusion && (
        <Card className="mt-6">
          <p className="flex items-start gap-2 text-[13.5px] text-muted-foreground">
            <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
            <span>Contactos y envíos masivos no están activados para tu organización. Si los necesitas, pídelo a soporte de Orion.</span>
          </p>
        </Card>
      )}
    </SectionLayout>
  );
}

/* ── Plantillas: aprobadas (Orión, en vivo) + administrar (mcp_django) ── */

function TemplatesSection({ notify, manage }: { notify: Notify; manage: boolean }) {
  const [data, setData] = useState<{ templates: WaTemplate[]; motivo?: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { getTemplates().then(setData).catch((e) => setErr(e?.message || 'Error')); }, []);
  return (
    <>
      <H title="Plantillas aprobadas" sub="Las únicas que WhatsApp deja enviar cuando pasaron 24 horas desde el último mensaje del cliente." />
      {!data && !err && <div className="h-40 animate-pulse rounded-2xl bg-muted" aria-hidden />}
      {err && <Card><p className="text-[14px] text-destructive">{err}</p></Card>}
      {data && data.templates.length === 0 && <Card><p className="text-[14px]">{data.motivo || 'Tu número todavía no tiene plantillas aprobadas.'}</p></Card>}
      <div className="space-y-3">
        {data?.templates.map((t) => (
          <Card key={`${t.name}-${t.language}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[13.5px] font-semibold">{t.name}</span>
              <span className="rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">{t.language}</span>
              {t.category && <span className="rounded-md border border-border px-1.5 py-0.5 text-[11px] text-muted-foreground">{t.category === 'MARKETING' ? 'Marketing' : t.category === 'UTILITY' ? 'Utilidad' : t.category}</span>}
              <span className={`ml-auto text-[12px] ${t.sendable ? 'wa-accent-text' : 'text-muted-foreground'}`}>{t.sendable ? 'Se envía desde los chats' : `Solo desde Meta: ${t.why_not}`}</span>
            </div>
            <p className="mt-2 whitespace-pre-wrap break-words rounded-xl bg-muted/60 px-3 py-2 text-[13.5px]">
              {t.body.split(/(\{\{\s*\w+\s*\}\})/g).map((part, i) => /^\{\{/.test(part)
                ? <mark key={i} className="rounded bg-amber-500/20 px-0.5 text-foreground">{part}</mark> : <span key={i}>{part}</span>)}
            </p>
          </Card>
        ))}
      </div>
      {manage && (
        <details className="group mt-6 rounded-2xl border border-border bg-card">
          <summary className="flex cursor-pointer list-none items-center gap-2 rounded-2xl px-4 py-3 text-[14px] font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--wa-accent))]">
            Crear y administrar plantillas <ChevronDown className="ml-auto h-4 w-4 transition-transform group-open:rotate-180" aria-hidden />
          </summary>
          <div className="border-t border-border p-4">
            <WhatsAppTemplatesPage embedded addNotification={(n) => notify(n.type, n.title, n.message)} />
          </div>
        </details>
      )}
    </>
  );
}
